import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from './common/SafeIcon';
import HeroSection from './components/HeroSection';
import MineSection from './components/MineSection';
import GradingSection from './components/GradingSection';
import CuttingSection from './components/CuttingSection';
import CertificationSection from './components/CertificationSection';
import JourneySection from './components/JourneySection';
import ConclusionSection from './components/ConclusionSection';
import ProgressBar from './components/ProgressBar';
import LanguageSelector from './components/LanguageSelector';
import { useTranslation } from './utils/i18n';
import './App.css';

const { FiVolume2, FiVolumeX } = FiIcons;

function App() {
  const [currentStep, setCurrentStep] = useState(0);
  const [isAudioEnabled, setIsAudioEnabled] = useState(false);
  const [hasStarted, setHasStarted] = useState(false);
  const [language, setLanguage] = useState('zh-TW');
  const { t } = useTranslation(language);

  const steps = [
    { id: 'hero', title: t('hero'), component: HeroSection },
    { id: 'mine', title: t('mine'), component: MineSection },
    { id: 'grading', title: t('grading'), component: GradingSection },
    { id: 'cutting', title: t('cutting'), component: CuttingSection },
    { id: 'certification', title: t('certification'), component: CertificationSection },
    { id: 'journey', title: t('journey'), component: JourneySection },
    { id: 'conclusion', title: t('conclusion'), component: ConclusionSection }
  ];

  const nextStep = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const goToStep = (stepIndex) => {
    setCurrentStep(stepIndex);
  };

  const startJourney = () => {
    setHasStarted(true);
    setCurrentStep(1);
  };

  const toggleAudio = () => {
    setIsAudioEnabled(!isAudioEnabled);
  };

  // Expose global functions for WordPress integration
  useEffect(() => {
    window.DiamondJourney = {
      goToStep,
      setLanguage: (lang) => setLanguage(lang),
      getCurrentStep: () => currentStep,
      config: {}
    };
  }, [currentStep]);

  const CurrentComponent = steps[currentStep].component;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-pantone-blue to-slate-900 text-white overflow-hidden relative">
      {/* Language Selector */}
      <LanguageSelector 
        currentLanguage={language} 
        onLanguageChange={setLanguage} 
      />


      {/* Progress Bar */}
      {hasStarted && (
        <ProgressBar 
          currentStep={currentStep} 
          totalSteps={steps.length} 
        />
      )}

      {/* Main Content */}
      <AnimatePresence mode="wait">
        <motion.div
          key={`${currentStep}-${language}`}
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -50 }}
          transition={{ duration: 0.6, ease: "easeInOut" }}
          className="min-h-screen"
        >
          <CurrentComponent
            nextStep={nextStep}
            prevStep={prevStep}
            currentStep={currentStep}
            totalSteps={steps.length}
            startJourney={startJourney}
            hasStarted={hasStarted}
            language={language}
            t={t}
            config={{}}
          />
        </motion.div>
      </AnimatePresence>

      {/* Background Effects */}
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-pantone-blue/10 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl animate-pulse delay-1000"></div>
      </div>
    </div>
  );
}

export default App;